// frontend/src/pages/driver/DriverDashboard.jsx
import { useState, useEffect } from 'react';
import axios from 'axios';

const API = import.meta.env.VITE_API_URL || 'http://localhost:5000/api/v1';

const DriverDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [dateFilter, setDateFilter] = useState('today');

  useEffect(() => {
    fetchDashboard();
  }, [dateFilter]);

  const getDateRange = () => {
    const now = new Date();
    let startDate = new Date();
    let endDate = new Date();

    switch (dateFilter) {
      case 'today':
        startDate.setHours(0, 0, 0, 0);
        endDate = new Date();
        break;
      case 'yesterday':
        startDate.setDate(now.getDate() - 1);
        startDate.setHours(0, 0, 0, 0);
        endDate = new Date(startDate);
        endDate.setHours(23, 59, 59, 999);
        break;
      case 'week':
        startDate.setDate(now.getDate() - 7);
        startDate.setHours(0, 0, 0, 0);
        endDate = new Date();
        break;
      case 'month':
        startDate.setDate(now.getDate() - 30);
        startDate.setHours(0, 0, 0, 0);
        endDate = new Date();
        break;
      default:
        startDate.setHours(0, 0, 0, 0);
        endDate = new Date();
    }

    return {
      date_from: startDate.toISOString().split('T')[0],
      date_to: endDate.toISOString().split('T')[0]
    };
  };

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const dateRange = getDateRange();
      
      const response = await axios.get(`${API}/driver/dashboard`, {
        headers: { Authorization: `Bearer ${token}` },
        params: dateRange
      });
      
      if (response.data.success) {
        setData(response.data.data);
      }
    } catch (err) {
      console.error('Dashboard error:', err);
      setError(err.response?.data?.message || 'Мэдээлэл татахад алдаа гарлаа');
    } finally {
      setLoading(false);
    }
  };

  const getFilterLabel = () => {
    const labels = {
      today: 'Өнөөдөр',
      yesterday: 'Өчигдөр', 
      week: '7 хоног',
      month: '30 хоног'
    };
    return labels[dateFilter] || 'Өнөөдөр';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-500 font-medium">Уншиж байна...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4">
        <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-center">
          <span className="text-4xl mb-3 block">⚠️</span>
          <p className="font-semibold text-red-800 mb-2">Алдаа гарлаа</p>
          <p className="text-sm text-red-600 mb-4">{error}</p>
          <button 
            onClick={fetchDashboard}
            className="px-6 py-2 bg-red-100 text-red-700 rounded-xl font-medium hover:bg-red-200 transition"
          >
            Дахин оролдох
          </button>
        </div>
      </div>
    );
  }

  const { driver, today, cod_balance } = data || {};

  return (
    <div className="p-4 space-y-5">
     {/* Driver Info */}
      {driver && (
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm">Сайн байна уу!</p>
              <h3 className="font-bold text-xl text-gray-800">{driver.name}</h3>
              {driver.vehicle_number && (
                <p className="text-blue-600 text-sm mt-1">🚗 {driver.vehicle_number}</p>
              )}
            </div>
            <div className="text-right">
              <div className="flex items-center gap-1 text-yellow-500">
                <span>⭐</span>
                <span className="font-bold">{parseFloat(driver.rating || 0).toFixed(1)}</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {driver.successful_deliveries || 0}/{driver.total_deliveries || 0} хүргэлт
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Date Filter Dropdown */}
      <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium text-gray-600">📅 Хугацаа сонгох</label>
          <select
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            className="px-4 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer"
          >
            <option value="today">📅 Өнөөдөр</option>
            <option value="yesterday">⏪ Өчигдөр</option>
            <option value="week">📆 7 хоног</option>
            <option value="month">🗓️ 30 хоног</option>
          </select>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 gap-4">
        {/* Total */}
        <div className="bg-white rounded-3xl p-4 shadow-sm border border-gray-100 hover:shadow-md transition">
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 bg-gradient-to-br from-blue-400 to-blue-500 rounded-2xl flex items-center justify-center shadow-lg">
              <span className="text-2xl">📦</span>
            </div>
            <div>
              <p className="text-gray-500 text-xs font-medium">Нийт</p>
              <p className="font-bold text-3xl text-gray-800">{today?.total || 0}</p>
            </div>
          </div>
        </div>

        {/* In Transit */}
        <div className="bg-white rounded-3xl p-4 shadow-sm border border-gray-100 hover:shadow-md transition">
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 bg-gradient-to-br from-orange-400 to-orange-500 rounded-2xl flex items-center justify-center shadow-lg">
              <span className="text-2xl">🚚</span>
            </div>
            <div>
              <p className="text-gray-500 text-xs font-medium">Хүргэж байгаа</p>
              <p className="font-bold text-3xl text-orange-500">{today?.in_transit || 0}</p>
            </div>
          </div>
        </div>

        {/* Delivered */}
        <div className="bg-white rounded-3xl p-4 shadow-sm border border-gray-100 hover:shadow-md transition">
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 bg-gradient-to-br from-green-400 to-green-500 rounded-2xl flex items-center justify-center shadow-lg">
              <span className="text-2xl">✅</span>
            </div>
            <div>
              <p className="text-gray-500 text-xs font-medium">Хүргэсэн</p>
              <p className="font-bold text-3xl text-green-500">{today?.delivered || 0}</p>
            </div>
          </div>
        </div>

        {/* Postponed */}
        <div className="bg-white rounded-3xl p-4 shadow-sm border border-gray-100 hover:shadow-md transition">
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 bg-gradient-to-br from-amber-400 to-amber-500 rounded-2xl flex items-center justify-center shadow-lg">
              <span className="text-2xl">⏸️</span>
            </div>
            <div>
              <p className="text-gray-500 text-xs font-medium">Хойшилсон</p>
              <p className="font-bold text-3xl text-amber-500">{today?.postponed || 0}</p>
            </div>
          </div>
        </div>
      </div>

      {/* COD Summary Card */}
      <div className="bg-gradient-to-br from-emerald-500 via-green-500 to-teal-500 rounded-3xl p-5 text-white shadow-xl">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
            <span className="text-xl">💰</span>
          </div>
          <div>
            <h3 className="font-bold text-lg">Барааны төлбөр</h3>
            <p className="text-emerald-100 text-xs">{getFilterLabel()}</p>
          </div>
        </div>
        
        {/* Нийт дүн */}
        <div className="bg-white/10 rounded-2xl p-4 mb-3">
          <p className="text-emerald-100 text-sm mb-1">Нийт авах дүн</p>
          <p className="font-bold text-3xl">{(today?.total_cod || 0).toLocaleString()}₮</p>
        </div>

        {/* Цуглуулсан */}
        <div className="bg-white/10 rounded-2xl p-4 mb-3">
          <p className="text-emerald-100 text-sm mb-1">Цуглуулсан ({getFilterLabel()})</p>
          <p className="font-bold text-2xl">{(today?.collected_cod || 0).toLocaleString()}₮</p>
        </div>

        <div className="space-y-2 text-sm">
          <div className="flex justify-between items-center py-2 border-b border-white/20">
            <span className="text-emerald-100">Нийт цуглуулсан (бүх цаг)</span>
            <span className="font-semibold">{(cod_balance?.total_collected || 0).toLocaleString()}₮</span>
          </div>
          <div className="flex justify-between items-center py-2 border-b border-white/20">
            <span className="text-emerald-100">Шилжүүлсэн</span>
            <span className="font-semibold">{(cod_balance?.total_transferred || 0).toLocaleString()}₮</span>
          </div>
          <div className="flex justify-between items-center pt-2">
            <span className="font-semibold">Үлдэгдэл</span>
            <span className="font-bold text-xl bg-white/20 px-3 py-1 rounded-xl">
              {(cod_balance?.owed_to_company || 0).toLocaleString()}₮
            </span>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-3xl p-5 shadow-sm border border-gray-100">
        <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
          <span className="text-xl">⚡</span> Түргэн үйлдэл
        </h3>
        <div className="grid grid-cols-2 gap-3">
          <button 
            onClick={() => window.location.href = '/driver/deliveries'}
            className="py-4 px-4 bg-gradient-to-r from-blue-50 to-blue-100 text-blue-700 rounded-2xl font-semibold hover:from-blue-100 hover:to-blue-200 transition flex items-center justify-center gap-2 border border-blue-200"
          >
            <span className="text-xl">📋</span> 
            <span>Хүргэлтүүд</span>
          </button>
          <button 
            onClick={() => window.location.href = '/driver/deliveries?status=active'}
            className="py-4 px-4 bg-gradient-to-r from-orange-50 to-orange-100 text-orange-700 rounded-2xl font-semibold hover:from-orange-100 hover:to-orange-200 transition flex items-center justify-center gap-2 border border-orange-200"
          >
            <span className="text-xl">🚚</span> 
            <span>Идэвхтэй</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default DriverDashboard;